<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TelegramInternalApi from 'dashboard/api/telegramInternal';

const { t } = useI18n();

const isLoading = ref(false);
const reportRows = ref([]);
const agentRows = ref([]);

const totalCases = computed(() =>
  reportRows.value.reduce((sum, row) => sum + row.count, 0)
);

const buildReport = async () => {
  isLoading.value = true;
  reportRows.value = [];
  agentRows.value = [];
  try {
    const response = await TelegramInternalApi.getReports();
    reportRows.value = (response.data?.open_summary || []).map(row => ({
      category: row.category,
      owner: row.owner,
      count: Number(row.count || 0),
    }));
    agentRows.value = (response.data?.agent_report || []).map(row => ({
      agentName: row.agent_name,
      resolvedCount: Number(row.resolved_count || 0),
    }));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  buildReport();
});
</script>

<template>
  <div class="flex flex-col gap-4 py-6">
    <div class="rounded-lg border border-n-weak bg-n-solid-2 p-4">
      <h1 class="text-base font-semibold text-n-slate-12">
        {{ t('TELEGRAM_INTERNAL.REPORTS.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-11">
        {{ t('TELEGRAM_INTERNAL.REPORTS.DESCRIPTION') }}
      </p>
    </div>

    <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
      <div class="flex items-center justify-between">
        <div class="text-sm text-n-slate-12">
          {{ t('TELEGRAM_INTERNAL.REPORTS.TOTAL_OPEN_CASES') }}:
          {{ totalCases }}
        </div>
        <button
          type="button"
          class="rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12"
          @click="buildReport"
        >
          {{ t('TELEGRAM_INTERNAL.REPORTS.REFRESH') }}
        </button>
      </div>
    </div>

    <div class="overflow-x-auto rounded-lg border border-n-weak bg-n-solid-1">
      <table class="min-w-full text-sm">
        <thead class="bg-n-alpha-2 text-left text-n-slate-11">
          <tr>
            <th class="px-3 py-2">
              {{ t('TELEGRAM_INTERNAL.REPORTS.CATEGORY') }}
            </th>
            <th class="px-3 py-2">
              {{ t('TELEGRAM_INTERNAL.REPORTS.OWNER') }}
            </th>
            <th class="px-3 py-2">
              {{ t('TELEGRAM_INTERNAL.REPORTS.CASES') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in reportRows"
            :key="`${row.category}-${row.owner}`"
            class="border-t border-n-weak"
          >
            <td class="px-3 py-2 text-n-slate-12">{{ row.category }}</td>
            <td class="px-3 py-2 text-n-slate-12">{{ row.owner }}</td>
            <td class="px-3 py-2 text-n-slate-12">{{ row.count }}</td>
          </tr>
          <tr v-if="!reportRows.length && !isLoading">
            <td class="px-3 py-6 text-n-slate-11" colspan="3">
              {{ t('TELEGRAM_INTERNAL.TABLE.EMPTY') }}
            </td>
          </tr>
          <tr v-if="isLoading">
            <td class="px-3 py-6 text-n-slate-11" colspan="3">
              {{ t('TELEGRAM_INTERNAL.LOADING') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="overflow-x-auto rounded-lg border border-n-weak bg-n-solid-1">
      <table class="min-w-full text-sm">
        <thead class="bg-n-alpha-2 text-left text-n-slate-11">
          <tr>
            <th class="px-3 py-2">
              {{ t('TELEGRAM_INTERNAL.REPORTS.AGENT') }}
            </th>
            <th class="px-3 py-2">
              {{ t('TELEGRAM_INTERNAL.REPORTS.RESOLVED') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in agentRows"
            :key="row.agentName"
            class="border-t border-n-weak"
          >
            <td class="px-3 py-2 text-n-slate-12">{{ row.agentName }}</td>
            <td class="px-3 py-2 text-n-slate-12">{{ row.resolvedCount }}</td>
          </tr>
          <tr v-if="!agentRows.length && !isLoading">
            <td class="px-3 py-6 text-n-slate-11" colspan="2">
              {{ t('TELEGRAM_INTERNAL.TABLE.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
